import React, { useState } from "react";
import { useSelector } from "react-redux";

import {
  Settings,
  Edit,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Lock,
  AlertCircle,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL } from "../../api/header";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader, PageHeaderHeading } from "@/components/ui/page-header";
import { PageContainer } from "@/components/layout/PageContainer";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Spinner } from "@/components/ui/spinner";
import RecordNotFound from "@/core/components/RecordNotFound";

const TenantSettings = () => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [resetGroupKey, setResetGroupKey] = useState(null);
  const [formData, setFormData] = useState({
    id: "",
    key: "",
    value: "",
    type: "",
    group: "",
    description: "",
    is_sensitive: false,
    is_system: false,
    is_active: true,
    meta: null,
  });

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // ==================== QUERIES ====================
  const { data: settings = [], isLoading: settingsLoading } = useQuery({
    queryKey: ["tenant-settings"],
    queryFn: async () => {
      let allSettings = [];
      let totalPages = 1;

      // Fetch first page to get total pages
      const firstRes = await apiClient.get(
        `${API_BASE_URL}/tenant-setting?page=1`,
      );

      if (firstRes.status === 200) {
        allSettings = [...firstRes.data.data];
        totalPages = firstRes.data.meta.last_page;

        // Fetch remaining pages if any
        if (totalPages > 1) {
          const promises = [];
          for (let page = 2; page <= totalPages; page++) {
            promises.push(
              apiClient.get(`${API_BASE_URL}/tenant-setting?page=${page}`),
            );
          }

          const results = await Promise.all(promises);
          results.forEach((res) => {
            if (res.status === 200) {
              allSettings = [...allSettings, ...res.data.data];
            }
          });
        }
      }
      return allSettings;
    },
  });

  // ==================== MUTATIONS ====================
  const saveSettingMutation = useMutation({
    mutationFn: async (updatedSetting) => {
      return apiClient.put(
        `${API_BASE_URL}/tenant-setting/${updatedSetting.id}`,
        updatedSetting,
      );
    },
    onSuccess: () => {
      toast.success("Setting updated successfully");
      setModalOpen(false);
      queryClient.invalidateQueries(["tenant-settings"]);
      resetFormData();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update setting");
    },
  });

  const resetGroupMutation = useMutation({
    mutationFn: async (groupKey) => {
      return apiClient.post(
        `${API_BASE_URL}/settings/reset`,
        {
          group: groupKey,
          confirm: true,
        },
      );
    },
    onSuccess: (_, groupKey) => {
      const config = groupConfig[groupKey] || {
        name: groupKey.charAt(0).toUpperCase() + groupKey.slice(1),
      };
      toast.success(
        `${config.name} settings have been reset to default values`,
      );
      queryClient.invalidateQueries(["tenant-settings"]);
      setResetGroupKey(null);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to reset group settings",
      );
      setResetGroupKey(null);
    },
  });

  const resetFormData = () => {
    setFormData({
      id: "",
      key: "",
      value: "",
      type: "",
      group: "",
      description: "",
      is_sensitive: false,
      is_system: false,
      is_active: true,
      meta: null,
    });
  };

  // Group settings by category
  const groupedSettings = settings.reduce((groups, setting) => {
    const group = setting.group || "other";
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(setting);
    return groups;
  }, {});

  // Group display names and order
  const groupConfig = {
    general: {
      name: "General Settings",
      icon: Settings,
      order: 1,
      color: "bg-blue-500",
    },
    email: {
      name: "Email Configuration",
      icon: Settings,
      order: 2,
      color: "bg-purple-500",
    },
    inventory: {
      name: "Inventory Management",
      icon: Settings,
      order: 3,
      color: "bg-green-500",
    },
    pos: {
      name: "Point of Sale",
      icon: Settings,
      order: 4,
      color: "bg-orange-500",
    },
    payment: {
      name: "Payment Methods",
      icon: Settings,
      order: 5,
      color: "bg-teal-500",
    },
    notification: {
      name: "Notifications",
      icon: Settings,
      order: 6,
      color: "bg-yellow-500",
    },
    repair: {
      name: "Repair Management",
      icon: Settings,
      order: 7,
      color: "bg-red-500",
    },
    security: {
      name: "Security Settings",
      icon: Lock,
      order: 8,
      color: "bg-gray-700",
    },
    tax: {
      name: "Tax Configuration",
      icon: Settings,
      order: 9,
      color: "bg-indigo-500",
    },
    sms: {
      name: "SMS Settings",
      icon: Settings,
      order: 10,
      color: "bg-pink-500",
    },
    api: {
      name: "API Settings",
      icon: Settings,
      order: 11,
      color: "bg-cyan-500",
    },
  };

  const handleEdit = (setting) => {
    setFormData({
      id: setting.id,
      key: setting.key,
      value: setting.value,
      type: setting.type,
      group: setting.group,
      description: setting.description,
      is_sensitive: setting.is_sensitive,
      is_system: setting.is_system,
      is_active: setting.is_active,
      meta: setting.meta,
    });
    setModalOpen(true);
  };

  const handleSave = () => {
    let processedValue = formData.value;

    // Process value based on type
    if (formData.type === "boolean") {
      processedValue = processedValue === "true" || processedValue === true;
    } else if (formData.type === "integer") {
      processedValue = parseInt(processedValue);
    } else if (formData.type === "float" || formData.type === "decimal") {
      processedValue = parseFloat(processedValue);
    }

    const finalMeta =
      formData.meta && typeof formData.meta === "string"
        ? (() => {
          try {
            return JSON.parse(formData.meta);
          } catch (error) {
            console.log(error);
            return {};
          }
        })()
        : formData.meta && typeof formData.meta === "object"
          ? formData.meta
          : {};

    const updatedSetting = {
      id: formData.id,
      key: formData.key,
      value: processedValue,
      type: formData.type,
      group: formData.group,
      description: formData.description,
      is_sensitive: formData.is_sensitive,
      is_system: formData.is_system,
      is_active: formData.is_active,
      meta: finalMeta,
    };

    saveSettingMutation.mutate(updatedSetting);
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const renderSettingValue = (setting) => {
    if (setting.type === "boolean") {
      if (!hasPermission("tenant setting read")) {
        return <RecordNotFound />;
      }
      return (
        <Badge
          variant={setting.value ? "default" : "destructive"}
          className={setting.value ? "bg-green-500" : ""}
        >
          {setting.value ? "Enabled" : "Disabled"}
        </Badge>
      );
    }
    if (setting.is_sensitive && setting.value) {
      if (hasPermission("tenant setting view_sensitive")) {
        return <span className="font-medium">{String(setting.value)}</span>;
      }
      return <span className="text-muted-foreground">••••••••</span>;
    }
    if (!setting.value && setting.value !== 0 && setting.value !== false) {
      return <span className="text-muted-foreground">Not set</span>;
    }
    return <span className="font-medium">{String(setting.value)}</span>;
  };

  const renderFormInput = () => {
    const meta =
      formData.meta && typeof formData.meta === "string"
        ? (() => {
          try {
            return JSON.parse(formData.meta);
          } catch {
            return {};
          }
        })()
        : formData.meta && typeof formData.meta === "object"
          ? formData.meta
          : {};

    switch (formData.type) {
      case "boolean":
        return (
          <Select
            value={formData.value.toString()}
            onValueChange={(value) => setFormData({ ...formData, value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Enabled</SelectItem>
              <SelectItem value="false">Disabled</SelectItem>
            </SelectContent>
          </Select>
        );

      case "integer":
      case "float":
      case "decimal":
        return (
          <Input
            type="number"
            value={formData.value}
            min={meta?.min_value}
            max={meta?.max_value}
            step={formData.type === "integer" ? "1" : "0.01"}
            onChange={(e) =>
              setFormData({ ...formData, value: e.target.value })
            }
          />
        );

      case "email":
        return (
          <Input
            type="email"
            value={formData.value}
            onChange={(e) =>
              setFormData({ ...formData, value: e.target.value })
            }
          />
        );

      case "url":
        return (
          <Input
            type="url"
            value={formData.value}
            onChange={(e) =>
              setFormData({ ...formData, value: e.target.value })
            }
          />
        );

      case "text":
        return (
          <Textarea
            rows={3}
            value={formData.value}
            onChange={(e) =>
              setFormData({ ...formData, value: e.target.value })
            }
          />
        );

      default:
        if (meta?.options) {
          return (
            <Select
              value={formData.value}
              onValueChange={(value) => setFormData({ ...formData, value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select option..." />
              </SelectTrigger>
              <SelectContent>
                {meta.options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        return (
          <Input
            type={
              formData.is_sensitive &&
                !hasPermission("tenant setting view_sensitive")
                ? "password"
                : "text"
            }
            value={formData.value}
            onChange={(e) =>
              setFormData({ ...formData, value: e.target.value })
            }
          />
        );
    }
  };

  if (settingsLoading) {
    return (
      <div className="flex h-[400px] w-full items-center justify-center">
        <Spinner size={48} />
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Tenant Settings"
          subtitle="Configure system settings and preferences"
        />
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(groupedSettings)
          .sort(
            ([a], [b]) =>
              (groupConfig[a]?.order || 999) - (groupConfig[b]?.order || 999),
          )
          .map(([groupKey, groupSettings]) => {
            const config = groupConfig[groupKey] || {
              name: groupKey.charAt(0).toUpperCase() + groupKey.slice(1),
              icon: Settings,
              color: "bg-gray-500",
            };
            const isExpanded = expandedGroups[groupKey] || false;
            const IconComponent = config.icon;

            return (
              <Card key={groupKey} className="overflow-hidden">
                <CardHeader
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleGroup(groupKey)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <IconComponent className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{config.name}</CardTitle>
                        <CardDescription className="text-xs">
                          {groupSettings.length} setting
                          {groupSettings.length !== 1 ? "s" : ""}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasPermission("tenant setting delete") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setResetGroupKey(groupKey);
                          }}
                          title="Reset all settings in this group"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="space-y-4 pt-6">
                    {groupSettings.map((setting) => (
                      <div
                        key={setting.id}
                        className="p-4 border rounded-lg hover:border-primary/50 transition-colors"
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <h6 className="font-semibold text-sm">
                                {setting.key
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1),
                                  )
                                  .join(" ")}
                              </h6>
                              {setting.is_system && (
                                <Badge variant="outline" className="text-xs">
                                  <Lock className="h-3 w-3 mr-1" />
                                  System
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {setting.description}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                Value:
                              </span>
                              {renderSettingValue(setting)}
                            </div>
                          </div>
                          {!setting.is_system &&
                            hasPermission("tenant setting update") && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(setting)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                        </div>
                        {setting.is_system && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-amber-600">
                            <AlertCircle className="h-3 w-3" />
                            <span>System setting - cannot be modified</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
      </div>

      {/* Edit Setting Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Edit Setting
            </DialogTitle>
            <DialogDescription>
              Modify the setting value below. Changes will take effect
              immediately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Setting Name</Label>
              <Input
                value={formData.key
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label>{formData.description || "Value"}</Label>
              {renderFormInput()}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={saveSettingMutation.isPending}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveSettingMutation.isPending}
            >
              {saveSettingMutation.isPending ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Group Confirmation */}
      <AlertDialog
        open={!!resetGroupKey}
        onOpenChange={() => setResetGroupKey(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Settings Group?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to reset all settings in "
              {resetGroupKey &&
                (groupConfig[resetGroupKey]?.name || resetGroupKey)}
              " group to their default values. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resetGroupMutation.mutate(resetGroupKey)}
              className="bg-red-600 hover:bg-red-700"
            >
              Reset Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default TenantSettings;
